import { z } from "zod";

import { CommonModels } from "@/data/common/common.models";

export namespace BizProfileModels {
  /**
   * ProficiencyEnumSchema
   * @type { enum }
   */
  export const ProficiencyEnumSchema = z.enum(["Developing", "Foundational", "Intermediate", "Advanced", "Expert"]);
  export type ProficiencyEnum = z.infer<typeof ProficiencyEnumSchema>;
  export const ProficiencyEnum = ProficiencyEnumSchema.enum;

  /**
   * NameEnumSchema
   * @type { enum }
   */
  export const NameEnumSchema = z.enum(["linkedin", "instagram", "youtube", "facebook"]);
  export type NameEnum = z.infer<typeof NameEnumSchema>;
  export const NameEnum = NameEnumSchema.enum;

  /**
   * EmploymentTypeEnumSchema
   * @type { enum }
   */
  export const EmploymentTypeEnumSchema = z.enum([
    "Full-time",
    "Part-time",
    "Self-employed",
    "Freelance",
    "Contract",
    "Internship",
    "Apprenticeship",
    "Other",
  ]);
  export type EmploymentTypeEnum = z.infer<typeof EmploymentTypeEnumSchema>;
  export const EmploymentTypeEnum = EmploymentTypeEnumSchema.enum;

  /**
   * AchievementOrAwardCategoryEnumSchema
   * @type { enum }
   */
  export const AchievementOrAwardCategoryEnumSchema = z.enum([
    "Academic",
    "Professional",
    "Competition",
    "Recognition",
    "Other",
  ]);
  export type AchievementOrAwardCategoryEnum = z.infer<typeof AchievementOrAwardCategoryEnumSchema>;
  export const AchievementOrAwardCategoryEnum = AchievementOrAwardCategoryEnumSchema.enum;

  /**
   * BizProfileListStatusEnumSchema
   * @type { enum }
   */
  export const BizProfileListStatusEnumSchema = z.enum([
    "initialized",
    "queued",
    "processing",
    "processed",
    "processing_failed",
  ]);
  export type BizProfileListStatusEnum = z.infer<typeof BizProfileListStatusEnumSchema>;
  export const BizProfileListStatusEnum = BizProfileListStatusEnumSchema.enum;

  /**
   * SocialMediaDtoSchema
   * @type { object }
   * @property { string } name
   * @property { string } url
   */
  export const SocialMediaDtoSchema = z.object({ name: NameEnumSchema, url: z.string() });
  export type SocialMediaDto = z.infer<typeof SocialMediaDtoSchema>;

  /**
   * ContactDtoSchema
   * @type { object }
   * @property { string } email
   * @property { string } phone
   * @property { SocialMediaDto[] } social_media
   */
  export const ContactDtoSchema = z.object({
    email: z.string().nullable(),
    phone: z.string().nullable(),
    social_media: z.array(SocialMediaDtoSchema),
  });
  export type ContactDto = z.infer<typeof ContactDtoSchema>;

  /**
   * SkillDtoSchema
   * @type { object }
   * @property { string } name
   * @property { string } proficiency
   */
  export const SkillDtoSchema = z.object({ name: z.string(), proficiency: ProficiencyEnumSchema.nullable() });
  export type SkillDto = z.infer<typeof SkillDtoSchema>;

  /**
   * LocationDtoSchema
   * @type { object }
   * @property { string } city
   * @property { string } state
   * @property { string } country
   * @property { string } postal_code
   * @property { string } raw_address
   * @property { string } msa_code
   * @property { string } msa_title
   */
  export const LocationDtoSchema = z.object({
    city: z.string().nullable(),
    state: z.string().nullable(),
    country: z.string().nullish(),
    postal_code: z.string().nullish(),
    raw_address: z.string().nullish(),
    msa_code: z.string().nullish(),
    msa_title: z.string().nullish(),
  });
  export type LocationDto = z.infer<typeof LocationDtoSchema>;

  /**
   * PersonProfileDtoSchema
   * @type { object }
   * @property { string } name
   * @property { string } pronoun
   * @property { SkillDto[] } skills
   * @property { ContactDto } contact
   * @property { LocationDto } location
   */
  export const PersonProfileDtoSchema = z.object({
    name: z.string().nullable(),
    pronoun: z.string().nullish(),
    skills: z.array(SkillDtoSchema),
    contact: ContactDtoSchema,
    location: LocationDtoSchema,
  });
  export type PersonProfileDto = z.infer<typeof PersonProfileDtoSchema>;

  /**
   * PersonMetadataDtoSchema
   * @type { object }
   * @property { number } total_years_of_experience
   */
  export const PersonMetadataDtoSchema = z.object({ total_years_of_experience: z.number() }).partial();
  export type PersonMetadataDto = z.infer<typeof PersonMetadataDtoSchema>;

  /**
   * PersonDtoSchema
   * @type { object }
   * @property { PersonProfileDto } profile
   * @property { PersonMetadataDto } metadata
   */
  export const PersonDtoSchema = z.object({
    profile: PersonProfileDtoSchema,
    metadata: PersonMetadataDtoSchema.optional(),
  });
  export type PersonDto = z.infer<typeof PersonDtoSchema>;

  /**
   * PeriodDtoSchema
   * @type { object }
   * @property { string } start_date
   * @property { string } end_date
   */
  export const PeriodDtoSchema = z.object({ start_date: z.string().nullable(), end_date: z.string().nullable() });
  export type PeriodDto = z.infer<typeof PeriodDtoSchema>;

  /**
   * CompanyPositionDtoSchema
   * @type { object }
   * @property { PeriodDto } period
   * @property { LocationDto } location
   * @property { string } work_setting
   * @property { string } role_title
   * @property { string } description
   * @property { string } employment_type
   * @property { number } salary
   * @property { boolean } is_current
   * @property { string } bls_code
   * @property { string } bls_title
   * @property { string } id
   * @property { number } job_zone_id
   */
  export const CompanyPositionDtoSchema = z.object({
    period: PeriodDtoSchema,
    location: LocationDtoSchema,
    work_setting: z.string().nullable(),
    role_title: z.string().nullable(),
    description: z.string().nullable(),
    employment_type: EmploymentTypeEnumSchema.nullable(),
    salary: z.number().nullable(),
    is_current: z.boolean().optional(),
    bls_code: z.string().nullish(),
    bls_title: z.string().nullish(),
    id: z.string().optional(),
    job_zone_id: z.number().optional(),
  });
  export type CompanyPositionDto = z.infer<typeof CompanyPositionDtoSchema>;

  /**
   * CompanyDtoSchema
   * @type { object }
   * @property { CompanyPositionDto[] } positions
   * @property { string } name
   * @property { string } industry
   * @property { string } hash_id
   */
  export const CompanyDtoSchema = z.object({
    positions: z.array(CompanyPositionDtoSchema),
    name: z.string().nullable(),
    industry: z.string().nullable(),
    hash_id: z.string().nullish(),
  });
  export type CompanyDto = z.infer<typeof CompanyDtoSchema>;

  /**
   * EducationDtoSchema
   * @type { object }
   * @property { PeriodDto } period
   * @property { string } degree
   * @property { string } field_of_study
   * @property { string } institution_name
   * @property { LocationDto } location
   * @property { string } hash_id
   * @property { string } grade
   * @property { string } description
   */
  export const EducationDtoSchema = z.object({
    period: PeriodDtoSchema,
    degree: z.string().nullable(),
    field_of_study: z.string().nullable(),
    institution_name: z.string().nullable(),
    location: LocationDtoSchema,
    hash_id: z.string().optional(),
    grade: z.string().nullish(),
    description: z.string().nullish(),
  });
  export type EducationDto = z.infer<typeof EducationDtoSchema>;

  /**
   * VolunteerExperienceDtoSchema
   * @type { object }
   * @property { string } organization_name
   * @property { PeriodDto } period
   * @property { string } role
   * @property { string } description
   * @property { LocationDto } location
   */
  export const VolunteerExperienceDtoSchema = z.object({
    organization_name: z.string().nullable(),
    period: PeriodDtoSchema.nullable(),
    role: z.string().nullable(),
    description: z.string().nullable(),
    location: LocationDtoSchema.nullable(),
  });
  export type VolunteerExperienceDto = z.infer<typeof VolunteerExperienceDtoSchema>;

  /**
   * AchievementOrAwardDtoSchema
   * @type { object }
   * @property { string } title
   * @property { string } date
   * @property { string } category
   * @property { string } description
   * @property { string } issuer
   */
  export const AchievementOrAwardDtoSchema = z.object({
    title: z.string(),
    date: z.string().nullable(),
    category: AchievementOrAwardCategoryEnumSchema.nullable(),
    description: z.string().nullable(),
    issuer: z.string().nullable(),
  });
  export type AchievementOrAwardDto = z.infer<typeof AchievementOrAwardDtoSchema>;

  /**
   * LicenseOrCertificationDtoSchema
   * @type { object }
   * @property { string } name
   * @property { string } issuing_organization
   * @property { string } issue_date
   * @property { string } credential_id
   * @property { string } verification_url
   */
  export const LicenseOrCertificationDtoSchema = z.object({
    name: z.string(),
    issuing_organization: z.string().nullable(),
    issue_date: z.string().nullable(),
    credential_id: z.string().nullable(),
    verification_url: z.string().nullable(),
  });
  export type LicenseOrCertificationDto = z.infer<typeof LicenseOrCertificationDtoSchema>;

  /**
   * OthersDtoSchema
   * @type { object }
   * @property { AchievementOrAwardDto[] } achievements_or_awards
   * @property { LicenseOrCertificationDto[] } licenses_or_certifications
   * @property { VolunteerExperienceDto[] } volunteer_experiences
   */
  export const OthersDtoSchema = z.object({
    achievements_or_awards: z.array(AchievementOrAwardDtoSchema),
    licenses_or_certifications: z.array(LicenseOrCertificationDtoSchema),
    volunteer_experiences: z.array(VolunteerExperienceDtoSchema),
  });
  export type OthersDto = z.infer<typeof OthersDtoSchema>;

  /**
   * ProfileV2DataDtoSchema
   * @type { object }
   * @property { PersonDto } person
   * @property { CompanyDto[] } companies
   * @property { EducationDto[] } education
   * @property { OthersDto } others
   */
  export const ProfileV2DataDtoSchema = z.object({
    person: PersonDtoSchema,
    companies: z.array(CompanyDtoSchema),
    education: z.array(EducationDtoSchema),
    others: OthersDtoSchema,
  });
  export type ProfileV2DataDto = z.infer<typeof ProfileV2DataDtoSchema>;

  /**
   * SkillRankSchema
   * @type { object }
   * @property { string } skill
   * @property { number } rank
   */
  export const SkillRankSchema = z.object({ skill: z.string(), rank: z.number() });
  export type SkillRank = z.infer<typeof SkillRankSchema>;

  /**
   * ProfileJobSkillMatrixResponseDtoSchema
   * @type { object }
   * @property { string } title
   * @property { string } matrixCode
   * @property { SkillRank[] } skillRanks
   * @property { SkillRank[] } topSkills
   */
  export const ProfileJobSkillMatrixResponseDtoSchema = z.object({
    title: z.string(),
    matrixCode: z.string(),
    skillRanks: z.array(SkillRankSchema),
    topSkills: z.array(SkillRankSchema),
  });
  export type ProfileJobSkillMatrixResponseDto = z.infer<typeof ProfileJobSkillMatrixResponseDtoSchema>;

  /**
   * BizProfileListDtoSchema
   * @type { object }
   * @property { string } id
   * @property { string } name
   * @property { string } bizOrganizationId
   * @property { string } bizOrganizationName
   * @property { string } bizProfileGroupId
   * @property { string } bizProfileGroupName
   * @property { string } jobTitle
   * @property { string } jobPosition
   * @property { number } jobSalary
   * @property { number } impliedSalary
   * @property { string } status
   * @property { string } statusReason
   * @property { string[] } tags
   * @property { string } comment
   * @property { number } rating
   * @property { boolean } isFavorited
   * @property { boolean } isRead
   * @property { number } experienceYears
   * @property { string } location
   * @property { string } residentLocation
   * @property { string } city
   * @property { string } state
   * @property { string[] } degrees
   * @property { number } rank
   * @property { string } createdAt
   * @property { string } updatedAt
   */
  export const BizProfileListDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    bizOrganizationId: z.string(),
    bizOrganizationName: z.string().optional(),
    bizProfileGroupId: z.string().nullable(),
    bizProfileGroupName: z.string().nullish(),
    jobTitle: z.string().nullish(),
    jobPosition: z.string().nullish(),
    jobSalary: z.number().nullable(),
    impliedSalary: z.number().nullable(),
    status: BizProfileListStatusEnumSchema,
    statusReason: z.string().nullish(),
    tags: z.array(z.string()),
    comment: z.string().nullish(),
    rating: z.number().nullish(),
    isFavorited: z.boolean(),
    isRead: z.boolean(),
    experienceYears: z.number().nullish(),
    location: z.string().nullish(),
    residentLocation: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    degrees: z.array(z.string()).nullish(),
    rank: z.number().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  });
  export type BizProfileListDto = z.infer<typeof BizProfileListDtoSchema>;

  /**
   * BulkCreateBizProfileResponseDtoSchema
   * @type { object }
   * @property { BizProfileListDto[] } profiles
   */
  export const BulkCreateBizProfileResponseDtoSchema = z.object({ profiles: z.array(BizProfileListDtoSchema) });
  export type BulkCreateBizProfileResponseDto = z.infer<typeof BulkCreateBizProfileResponseDtoSchema>;

  /**
   * ExperienceYearsFilterDtoSchema
   * @type { object }
   * @property { number } min
   * @property { number } max
   */
  export const ExperienceYearsFilterDtoSchema = z.object({ min: z.number(), max: z.number() }).partial();
  export type ExperienceYearsFilterDto = z.infer<typeof ExperienceYearsFilterDtoSchema>;

  /**
   * BizProfileDeleteFilterDtoSchema
   * @type { object }
   * @property { string } search
   * @property { string } jobTitle
   * @property { string[] } tagName
   * @property { string } msaLocation
   * @property { string[] } rating
   * @property { ExperienceYearsFilterDto } experienceYears
   * @property { boolean } all
   */
  export const BizProfileDeleteFilterDtoSchema = z
    .object({
      search: z.string(),
      jobTitle: z.string(),
      tagName: z.array(z.string()),
      msaLocation: z.string(),
      rating: z.array(z.string()),
      experienceYears: ExperienceYearsFilterDtoSchema,
      all: z.boolean(),
    })
    .partial();
  export type BizProfileDeleteFilterDto = z.infer<typeof BizProfileDeleteFilterDtoSchema>;

  /**
   * BulkDeleteBizProfileRequestDtoSchema
   * @type { object }
   * @property { string[] } ids
   * @property { BizProfileDeleteFilterDto } filter
   */
  export const BulkDeleteBizProfileRequestDtoSchema = z.object({
    ids: z.array(z.string()),
    filter: BizProfileDeleteFilterDtoSchema.optional(),
  });
  export type BulkDeleteBizProfileRequestDto = z.infer<typeof BulkDeleteBizProfileRequestDtoSchema>;

  /**
   * BizProfileWorthScoresDtoSchema
   * @type { object }
   * @property { number } current
   * @property { number } historical
   * @property { number } implied
   */
  export const BizProfileWorthScoresDtoSchema = z.object({
    current: z.number(),
    historical: z.number(),
    implied: z.number(),
  });
  export type BizProfileWorthScoresDto = z.infer<typeof BizProfileWorthScoresDtoSchema>;

  /**
   * ShowBizProfileDtoSchema
   * @type { object }
   * @property { string } id
   * @property { string } bizProfileGroupId
   * @property { string } bizProfileGroupName
   * @property { string } jobTitle
   * @property { string } jobPosition
   * @property { number } jobSalary
   * @property { string } introductionBio
   * @property { CommonModels.BizTagDto[] } tags
   * @property { string } comment
   * @property { number } rating
   * @property { number } rank
   * @property { ProfileV2DataDto } dataV02
   * @property { boolean } isFavorited
   * @property { boolean } isRead
   * @property { number } experienceYears
   * @property { string } location
   * @property { string } city
   * @property { string } state
   * @property { string } createdAt
   * @property { string } updatedAt
   * @property { string } status
   * @property { string } statusReason
   * @property { BizProfileWorthScoresDto } worthScores
   */
  export const ShowBizProfileDtoSchema = z.object({
    id: z.string(),
    bizProfileGroupId: z.string().nullable(),
    bizProfileGroupName: z.string().nullish(),
    jobTitle: z.string(),
    jobPosition: z.string().nullish(),
    jobSalary: z.number().nullish(),
    introductionBio: z.string().nullish(),
    tags: z.array(CommonModels.BizTagDtoSchema),
    comment: z.string().nullish(),
    rating: z.number().nullish(),
    rank: z.number().nullish(),
    dataV02: ProfileV2DataDtoSchema.nullable(),
    isFavorited: z.boolean(),
    isRead: z.boolean(),
    experienceYears: z.number().nullish(),
    location: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    status: z.string(),
    statusReason: z.string(),
    worthScores: BizProfileWorthScoresDtoSchema.nullable(),
  });
  export type ShowBizProfileDto = z.infer<typeof ShowBizProfileDtoSchema>;

  /**
   * BulkCreateBizProfileRequestDtoSchema
   * @type { object }
   * @property { string[] } fileIds
   * @property { string[] } tags
   * @property { string } jobTitle
   * @property { string } location
   */
  export const BulkCreateBizProfileRequestDtoSchema = z.object({
    fileIds: z.array(z.string()),
    tags: z.array(z.string()),
    jobTitle: z.string(),
    location: z.string(),
  });
  export type BulkCreateBizProfileRequestDto = z.infer<typeof BulkCreateBizProfileRequestDtoSchema>;

  /**
   * BulkDeleteBizProfileResponseDtoSchema
   * @type { object }
   * @property { string[] } ids
   */
  export const BulkDeleteBizProfileResponseDtoSchema = z.object({ ids: z.array(z.string()) });
  export type BulkDeleteBizProfileResponseDto = z.infer<typeof BulkDeleteBizProfileResponseDtoSchema>;

  /**
   * BulkUpdateBizProfileRequestDtoSchema
   * @type { object }
   * @property { string[] } ids
   * @property { string[] } tags
   * @property { string } jobTitle
   * @property { string } location
   */
  export const BulkUpdateBizProfileRequestDtoSchema = z.object({
    ids: z.array(z.string()),
    tags: z.array(z.string()),
    jobTitle: z.string(),
    location: z.string(),
  });
  export type BulkUpdateBizProfileRequestDto = z.infer<typeof BulkUpdateBizProfileRequestDtoSchema>;

  /**
   * BulkUpdateBizProfileResponseDtoSchema
   * @type { object }
   * @property { string[] } ids
   */
  export const BulkUpdateBizProfileResponseDtoSchema = z.object({ ids: z.array(z.string()) });
  export type BulkUpdateBizProfileResponseDto = z.infer<typeof BulkUpdateBizProfileResponseDtoSchema>;

  /**
   * BizProfileLocationDtoSchema
   * @type { object }
   * @property { string } location
   * @property { string } city
   * @property { string } state
   * @property { number } count
   */
  export const BizProfileLocationDtoSchema = z.object({
    location: z.string(),
    city: z.string(),
    state: z.string(),
    count: z.number(),
  });
  export type BizProfileLocationDto = z.infer<typeof BizProfileLocationDtoSchema>;

  /**
   * BizProfileJobTitleDtoSchema
   * @type { object }
   * @property { string } jobTitle
   * @property { number } count
   */
  export const BizProfileJobTitleDtoSchema = z.object({ jobTitle: z.string(), count: z.number() });
  export type BizProfileJobTitleDto = z.infer<typeof BizProfileJobTitleDtoSchema>;

  /**
   * BizProfileJobPositionDtoSchema
   * @type { object }
   * @property { string } jobPosition
   * @property { number } count
   */
  export const BizProfileJobPositionDtoSchema = z.object({ jobPosition: z.string(), count: z.number() });
  export type BizProfileJobPositionDto = z.infer<typeof BizProfileJobPositionDtoSchema>;

  /**
   * BizProfileMarkAllAsReadRequestDtoSchema
   * @type { object }
   * @property { string[] } ids
   */
  export const BizProfileMarkAllAsReadRequestDtoSchema = z.object({ ids: z.array(z.string()) });
  export type BizProfileMarkAllAsReadRequestDto = z.infer<typeof BizProfileMarkAllAsReadRequestDtoSchema>;

  /**
   * UpdateBizProfileRequestDtoSchema
   * @type { object }
   * @property { string } comment
   * @property { number } rating
   * @property { string[] } tagIds
   */
  export const UpdateBizProfileRequestDtoSchema = z
    .object({ comment: z.string(), rating: z.number(), tagIds: z.array(z.string()) })
    .partial();
  export type UpdateBizProfileRequestDto = z.infer<typeof UpdateBizProfileRequestDtoSchema>;

  /**
   * DeleteBizProfileResponseDtoSchema
   * @type { object }
   * @property { string } id
   */
  export const DeleteBizProfileResponseDtoSchema = z.object({ id: z.string() });
  export type DeleteBizProfileResponseDto = z.infer<typeof DeleteBizProfileResponseDtoSchema>;

  /**
   * FavoriteBizProfileResponseDtoSchema
   * @type { object }
   * @property { string } id
   */
  export const FavoriteBizProfileResponseDtoSchema = z.object({ id: z.string() });
  export type FavoriteBizProfileResponseDto = z.infer<typeof FavoriteBizProfileResponseDtoSchema>;

  /**
   * UnfavoriteBizProfileResponseDtoSchema
   * @type { object }
   * @property { string } id
   */
  export const UnfavoriteBizProfileResponseDtoSchema = z.object({ id: z.string() });
  export type UnfavoriteBizProfileResponseDto = z.infer<typeof UnfavoriteBizProfileResponseDtoSchema>;

  /**
   * RetryProcessingPipelineResponseDtoSchema
   * @type { object }
   * @property { string } id
   */
  export const RetryProcessingPipelineResponseDtoSchema = z.object({ id: z.string() });
  export type RetryProcessingPipelineResponseDto = z.infer<typeof RetryProcessingPipelineResponseDtoSchema>;

  /**
   * ProfileResumeDownloadResponseDtoSchema
   * @type { object }
   * @property { string } url
   * @property { string } mimeType
   */
  export const ProfileResumeDownloadResponseDtoSchema = z.object({ url: z.string(), mimeType: z.string() });
  export type ProfileResumeDownloadResponseDto = z.infer<typeof ProfileResumeDownloadResponseDtoSchema>;

  /**
   * BizProfileListFilterRatingParamSchema
   * @type { array }
   * @description E,x,a,m,p,l,e,:, ,`,1,`
   */
  export const BizProfileListFilterRatingParamSchema = z.array(z.number()).optional();
  export type BizProfileListFilterRatingParam = z.infer<typeof BizProfileListFilterRatingParamSchema>;

  /**
   * BizProfileListFilterTagNameParamSchema
   * @type { array }
   * @description E,x,a,m,p,l,e,:, ,`,P,r,o,f,e,s,s,i,o,n,a,l,`
   */
  export const BizProfileListFilterTagNameParamSchema = z.array(z.string()).optional();
  export type BizProfileListFilterTagNameParam = z.infer<typeof BizProfileListFilterTagNameParamSchema>;

  /**
   * BizProfileListResponseSchema
   * @type { object }
   * @property { object[] } order List of order definitions
   * @property { number } page 1-indexed page number to begin from
   * @property { string } cursor ID of item to start after
   * @property { string } nextCursor Cursor for next set of items
   * @property { number } limit Items per response
   * @property { number } totalItems Total available items
   * @property { BizProfileListDto[] } items
   */
  export const BizProfileListResponseSchema = CommonModels.PaginationDtoSchema.merge(
    z.object({ items: z.array(BizProfileListDtoSchema) }).partial(),
  );
  export type BizProfileListResponse = z.infer<typeof BizProfileListResponseSchema>;

  /**
   * ListLocationsResponseSchema
   * @type { object }
   * @property { object[] } order List of order definitions
   * @property { number } page 1-indexed page number to begin from
   * @property { string } cursor ID of item to start after
   * @property { string } nextCursor Cursor for next set of items
   * @property { number } limit Items per response
   * @property { number } totalItems Total available items
   * @property { BizProfileLocationDto[] } items
   */
  export const ListLocationsResponseSchema = CommonModels.PaginationDtoSchema.merge(
    z.object({ items: z.array(BizProfileLocationDtoSchema) }).partial(),
  );
  export type ListLocationsResponse = z.infer<typeof ListLocationsResponseSchema>;

  /**
   * ListJobTitlesResponseSchema
   * @type { object }
   * @property { object[] } order List of order definitions
   * @property { number } page 1-indexed page number to begin from
   * @property { string } cursor ID of item to start after
   * @property { string } nextCursor Cursor for next set of items
   * @property { number } limit Items per response
   * @property { number } totalItems Total available items
   * @property { BizProfileJobTitleDto[] } items
   */
  export const ListJobTitlesResponseSchema = CommonModels.PaginationDtoSchema.merge(
    z.object({ items: z.array(BizProfileJobTitleDtoSchema) }).partial(),
  );
  export type ListJobTitlesResponse = z.infer<typeof ListJobTitlesResponseSchema>;

  /**
   * ListJobPositionsResponseSchema
   * @type { object }
   * @property { object[] } order List of order definitions
   * @property { number } page 1-indexed page number to begin from
   * @property { string } cursor ID of item to start after
   * @property { string } nextCursor Cursor for next set of items
   * @property { number } limit Items per response
   * @property { number } totalItems Total available items
   * @property { BizProfileJobPositionDto[] } items
   */
  export const ListJobPositionsResponseSchema = CommonModels.PaginationDtoSchema.merge(
    z.object({ items: z.array(BizProfileJobPositionDtoSchema) }).partial(),
  );
  export type ListJobPositionsResponse = z.infer<typeof ListJobPositionsResponseSchema>;
}
